import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@material-ui/core';

import { useJiraConnectContext } from '../JiraConnect.context';
import { JiraApiProperties, JiraIssueField } from '../JiraConnect.interface';
import JiraReferenceTypeLinks from './JiraReferenceTypeLinks';

const StyledBox = styled(Box)`
  padding-top: 16px;
  padding-left: 16px;
  .type {
    margin-top: 2px;
    margin-bottom: 2px;
  }
  .description {
    white-space: break-spaces;
  }
`;

export interface JiraReferenceTypeListProps {
  show: boolean;
  className?: string;
  keyOrIssueField: JiraIssueField | string;
  onClickType?: (schemaKey: string) => void;
  onFindProperties?: (properties: JiraApiProperties | undefined) => void;
}

const JiraReferenceTypeList: React.FC<JiraReferenceTypeListProps> = (
  props: JiraReferenceTypeListProps
) => {
  const {
    show = false,
    className,
    keyOrIssueField,
    onClickType,
    onFindProperties
  } = props;
  const [didOpen, setDidOpen] = useState<boolean>(show);
  const { getSchemaProperties } = useJiraConnectContext();

  const properties: JiraApiProperties | undefined = useMemo(() => {
    return getSchemaProperties(keyOrIssueField);
  }, [keyOrIssueField, getSchemaProperties]);

  // only render items if opened to avoid lag
  const items: JSX.Element[] = useMemo(() => {
    if (!didOpen || !properties) {
      return [];
    }
    return Object.entries(properties).map(([key, property]) => {
      const { type, allOf = [], description } = property;
      const hasRefs: boolean = allOf.length > 0;
      const shouldWrap: boolean = !!type && hasRefs;
      return (
        <ListItem key={key}>
          <ListItemText
            primary={key}
            primaryTypographyProps={{ style: { fontSize: 16 } }}
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <>
                {(type || hasRefs) && (
                  <Typography
                    variant='body2'
                    component='div'
                    className='type'
                    color='textPrimary'
                  >
                    <code>
                      {type && (
                        <>
                          {type} {shouldWrap && '('}
                        </>
                      )}
                      {hasRefs && (
                        <JiraReferenceTypeLinks
                          refs={allOf}
                          onClick={onClickType}
                        />
                      )}
                      {shouldWrap && ')'}
                    </code>
                  </Typography>
                )}
                {description && (
                  <div className='description'>{description}</div>
                )}
              </>
            }
          />
        </ListItem>
      );
    });
  }, [didOpen, properties, onClickType]);

  useEffect(() => {
    show && setDidOpen(true);
  }, [show]);

  useEffect(() => {
    onFindProperties?.(properties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties]);

  return (
    <StyledBox className={className}>
      <Box mr={2}>
        <Typography variant='overline'>Available Properties</Typography>
      </Box>
      <List>{items}</List>
    </StyledBox>
  );
};

export default JiraReferenceTypeList;
